import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import FeatureCard from './FeatureCard/FeatureCard';
import adventureOfflineSrc from './images/adventure-offline.avif';
import conditionsUSVariantSrc from './images/conditions-us-variant.avif';
import conditionsSrc from './images/conditions.avif';
import wrongTurnAlertsSrc from './images/wrong-turn-alerts.avif';
import styles from './PlusFeatureList.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

export default function PlusFeatureList({ isUSVariant }: { isUSVariant: boolean }) {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const params = new URLSearchParams(useSearchParams());
  const search = params.toString();
  const checkoutUrl = wrapUrlSafe(`/plans/checkout${search.length > 0 ? `?${search}` : ''}`, languageRegionCode);

  return (
    <div className={styles.container}>
      <Heading as="h2" className={styles.header} size="500">
        <FormattedMessage defaultMessage="Let AllTrails be your guide" />
      </Heading>
      <div className={styles.cardsContainer}>
        <FeatureCard
          title={<FormattedMessage defaultMessage="Adventure offline" />}
          description={<FormattedMessage defaultMessage="Download trails, parks, and areas to stay on route without cell signal." />}
          src={adventureOfflineSrc}
          altText={intl.formatMessage({ defaultMessage: 'Offline Maps feature in the AllTrails app' })}
        />
        <FeatureCard
          title={<FormattedMessage defaultMessage="Plan for on-trail conditions" />}
          description={<FormattedMessage defaultMessage="Check trail-specific weather, snow depth, mud, ice, and more before you go." />}
          altText={intl.formatMessage({ defaultMessage: 'On-trail conditions in the AllTrails app' })}
          src={isUSVariant ? conditionsUSVariantSrc : conditionsSrc}
        />
        <FeatureCard
          title={<FormattedMessage defaultMessage="Get alerts for wrong turns" />}
          description={<FormattedMessage defaultMessage="We'll notify you before you stray too far from your planned route." />}
          src={wrongTurnAlertsSrc}
          altText={intl.formatMessage({ defaultMessage: 'Wrong turn alerts in the AllTrails app' })}
        />
      </div>
      <div className={styles.buttonContainer}>
        <Button
          text={<FormattedMessage defaultMessage="Start your free trial" />}
          {...(user ? { linkInfo: { href: checkoutUrl } } : { onClick: () => setIsSignUpModalOpen(true) })}
          variant="primary"
        />
      </div>
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.location.assign(checkoutUrl);
          }}
          trigger="Upgrade"
          upsellTrigger="Upgrade"
          hideUpsell
        />
      )}
    </div>
  );
}
